import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gtbuyspreadsheet.work';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-4xl font-heading font-semibold text-foreground-900 mb-4">Article Not Found</h1>
          <p className="text-foreground-500 mb-6">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Back to Homepage
          </Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    publisher: {
      '@type': 'Organization',
      name: 'GTBuySpreadsheet',
      url: siteUrl + '/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl + '/article/' + article.slug,
    },
    image: article.imageUrl,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.content.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <article>
        <header className="relative w-full h-[340px] md:h-[420px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col justify-end px-4 md:px-10 pb-10 md:pb-14">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 text-white/70 text-xs mb-4 flex-wrap">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  Home
                </Link>
                <span>/</span>
                <span className="text-white/90">{article.title.length > 50 ? article.title.slice(0, 50) + '...' : article.title}</span>
              </nav>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-10 pb-8 border-b border-background-200/70">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-user-line text-accent-600 text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{article.author}</p>
              <p className="text-xs text-foreground-500 whitespace-nowrap">System Analyst &amp; Data Engineer</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-foreground-500 whitespace-nowrap">Last updated: {article.dateModified}</p>
              <p className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</p>
            </div>
          </div>

          <div className="prose-custom">
            <section className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                {article.content.intro.heading}
              </h2>
              {article.content.intro.paragraphs.map((p, i) => (
                <p key={i} className="text-foreground-600 leading-relaxed mb-4">
                  {p}
                </p>
              ))}
            </section>

            {article.content.sections.map((section, i) => (
              <section key={i} className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="text-foreground-600 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}

                {i === article.content.sections.findIndex((s) => s.heading.includes('Comparison')) && article.content.table && (
                  <div className="overflow-x-auto mb-6 mt-6">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-background-200/70">
                          {article.content.table.headers.map((h, idx) => (
                            <th key={idx} className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${idx === 1 ? 'text-primary-600' : idx === 0 ? 'text-foreground-900' : 'text-foreground-700'}`}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="text-foreground-600">
                        {article.content.table.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-background-100">
                            {row.map((cell, ci) => (
                              <td key={ci} className={`py-3 px-4 ${ci === 0 ? 'font-medium text-foreground-800 whitespace-nowrap' : ''}`}>
                                {ci === 1 ? (
                                  <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full whitespace-nowrap">{cell}</span>
                                ) : (
                                  cell
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            {article.content.steps && (
              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Step-by-Step Guide
                </h2>
                <div className="space-y-4">
                  {article.content.steps.map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-semibold">
                        {i + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 mb-1">{step.title}</h4>
                        <p className="text-sm text-foreground-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {article.content.useCases && (
              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                  Key Use Cases
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {article.content.useCases.map((uc, i) => (
                    <div key={i} className="p-5 rounded-lg bg-background-100">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                        <i className={uc.icon}></i>
                      </div>
                      <h4 className="font-semibold text-foreground-900 mb-1 text-sm whitespace-nowrap">{uc.title}</h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">{uc.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {article.content.faqs.map((faq, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-100">
                    <h4 className="font-semibold text-foreground-900 mb-2 text-sm">{faq.question}</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            <aside className="mt-12 pt-8 border-t border-background-200/70">
              <h4 className="text-sm font-semibold text-foreground-900 mb-4">Related Resources</h4>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 text-foreground-700 text-sm rounded-full hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-line w-3 h-3 flex items-center justify-center"></i>
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Visit KASpreadsheet
                  <i className="ri-arrow-right-up-line w-3 h-3 flex items-center justify-center"></i>
                </a>
              </div>
            </aside>

            <div className="mt-10 p-6 rounded-lg bg-background-100 border border-background-200/70">
              <p className="text-xs text-foreground-400 leading-relaxed">
                <strong className="text-foreground-500">Disclaimer:</strong> This guide is provided for informational purposes only. Spreadsheet features, platform capabilities, and pricing may change over time. Always verify current functionality directly with each platform before making business decisions based on the comparisons presented here.
              </p>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}